import type { NftAsset } from '@properwallet/chain-eos'
import { useTranslation } from '../hooks/useTranslation'
import { useGatewayImage } from '../hooks/useGatewayImage'

export interface NftGalleryProps {
  nfts: NftAsset[]
  nftsLoading: boolean
  onSelectAvatar?: (assetId: string, url: string) => void
  /** The asset currently used as the avatar, so the grid can mark it. */
  selectedAssetId?: string | null
  /** Shows a "Send" button per card — opens the caller's own transfer flow. */
  onSend?: (assetId: string, name: string) => void
}

export default function NftGallery({
  nfts,
  nftsLoading,
  onSelectAvatar,
  selectedAssetId = null,
  onSend,
}: NftGalleryProps) {
  const { t } = useTranslation()

  if (nftsLoading) {
    return (
      <div className="grid grid-cols-2 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="rounded-xl bg-surface-2 aspect-square animate-pulse border border-surface-3"
          />
        ))}
      </div>
    )
  }

  if (nfts.length === 0) {
    return (
      <p className="text-center text-gray-500 text-sm py-12 px-4">{t('nft.noNfts')}</p>
    )
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      {nfts.map(nft => {
        const canPick = Boolean(onSelectAvatar && nft.mediaUrl)
        const isSelected = selectedAssetId != null && selectedAssetId === nft.assetId
        const handlePick = () => {
          if (canPick) onSelectAvatar!(nft.assetId, nft.mediaUrl!)
        }

        return (
          <div
            key={nft.assetId}
            className={`card p-2 flex flex-col gap-2 overflow-hidden border ${
              isSelected ? 'border-brand' : 'border-surface-3'
            }`}
          >
            <div
              role={canPick ? 'button' : undefined}
              tabIndex={canPick ? 0 : undefined}
              onClick={canPick ? handlePick : undefined}
              onKeyDown={
                canPick
                  ? e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handlePick()
                      }
                    }
                  : undefined
              }
              className={`relative aspect-square rounded-lg bg-surface-2 overflow-hidden flex items-center justify-center ${
                canPick ? 'cursor-pointer active:opacity-90' : ''
              }`}
            >
              <NftThumb mediaUrl={nft.mediaUrl} mediaType={nft.mediaType} />
            </div>
            <div className="min-w-0 px-0.5">
              <p className="text-white text-xs font-medium truncate">{nft.name}</p>
              <p className="text-gray-500 text-[10px] truncate">{nft.collection}</p>
            </div>
            {onSelectAvatar && nft.mediaUrl && (
              <button
                type="button"
                className={`text-[11px] py-1.5 w-full ${isSelected ? 'btn-primary' : 'btn-secondary'}`}
                onClick={handlePick}
                disabled={isSelected}
              >
                {isSelected ? t('nft.currentAvatar') : t('nft.setAvatar')}
              </button>
            )}
            {onSelectAvatar && !nft.mediaUrl && (
              <p className="text-[10px] text-gray-600 text-center">{t('nft.noPreview')}</p>
            )}
            {onSend && (
              <button
                type="button"
                className="btn-ghost text-[11px] py-1.5 w-full"
                onClick={() => onSend(nft.assetId, nft.name)}
              >
                {t('nft.send')}
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}

/**
 * One grid cell's media, isolated so `useGatewayImage` — which cycles through
 * IPFS gateways on failure — runs per item instead of once for the whole grid.
 */
function NftThumb({
  mediaUrl,
  mediaType,
}: {
  mediaUrl: string | null
  mediaType: NftAsset['mediaType']
}) {
  const { src, failed, onError, imgProps } = useGatewayImage(mediaUrl)
  const isVideo = mediaType === 'video'

  if (!mediaUrl || failed || !src) {
    return (
      <span className="text-4xl text-gray-600" aria-hidden>
        🖼️
      </span>
    )
  }

  if (isVideo) {
    return (
      <video
        key={src}
        src={src}
        className="w-full h-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        onError={onError}
        {...imgProps}
      />
    )
  }

  return (
    <img
      key={src}
      src={src}
      alt=""
      loading="lazy"
      className="w-full h-full object-cover"
      onError={onError}
      {...imgProps}
    />
  )
}

